import React, {useCallback, useReducer} from "react";
import { SelectChangeEvent } from "@mui/material/Select";
import { IFormCreateTestTraining } from "features/tests/create/components/_interfaces";
import {TestType} from "shared/types/testTypes";
import { UseFormClearErrors, UseFormSetError } from "react-hook-form";
import {
    MAX_SELECTED_STANDARDS,
    selectedStandardsCount, updateItemAtIndex
} from "features/tests/create/utils/functions";
import {StandardType} from "../../../../utils/testsStandards/_standardType";
import {ICategory} from "../../../../shared/types/testCategoriesTypes";
import UseTestFormDataFetching from "./useTestFormDataFetching";
import UseCreateTestFormSubmit from "./useTestSubmission";
import useCreateTestStepsWizard from "./useTestWizardStep";
import UseClassroomSelection from "./useClassroomSelection";

interface UseCreateTestFormOptions {
    type: TestType;
    multipleClassrooms?: boolean;
    isWorkPlan?: boolean;
    setError?: UseFormSetError<any>;
    clearErrors?: UseFormClearErrors<any>;
}

export const getInitialTestFormState = (): IFormCreateTestTraining => ({
    classroom: null,
    classrooms: [],
    users: [],
    test_config: {
        value: "1",
        label: "Elevul poate rezolva itemii in orice ordine si se poate reintoarce asupra raspunsurilor la itemi.",
    },
    difficulty: {value: "", label: ""},
    items_number: {value: "", label: ""},
    test_duration: {value: "", label: ""},
    individual_test: {value: "1", label: "Teste diferite pentru fiecare elev"},
    start_date: new Date(),
    expire: "",
    custom_duration: "",
    subject: {value: "", label: ""},
    grade: {value: "", label: "", gradeId: ""},
    categories: [],
    category: {value: "", label: ""},
    tag: {value: "", label: ""},
    variant: {value: "", label: ""},
    items_ids: "",
});

const reducer = (state: IFormCreateTestTraining, action: any) => {
    switch (action.type) {
        case 'classroom':
            return {
                ...state,
                classroom: action.payload,
                users: [],
            }
        case 'classrooms':
            return {
                ...state,
                classrooms: action.payload,
            }
        case 'users':
            return {
                ...state,
                users: action.payload,
            }
        case 'test_config':
            return {
                ...state,
                test_config: action.payload,
            }
        case 'difficulty':
            return {
                ...state,
                difficulty: action.payload,
                categories: [],
                category: {value: "", label: ""},
            }
        case 'items_number':
            return {
                ...state,
                items_number: action.payload,
            }
        case 'test_duration':
            return {
                ...state,
                test_duration: action.payload,
            }
        case 'individual_test':
            return {
                ...state,
                individual_test: action.payload,
            }
        case 'start_date':
            return {
                ...state,
                start_date: action.payload,
            }
        case 'expire':
            return {
                ...state,
                expire: action.payload,
            }
        case 'custom_duration':
            return {
                ...state,
                custom_duration: action.payload,
            }
        case 'subject':
            return {
                ...state,
                subject: action.payload,
                grade: {value: "", label: "", gradeId: ""},
                categories: [],
                category: {value: "", label: ""},
                variant: {value: "", label: ""},
            }
        case 'type':
            return {
                ...state,
                subject: action.payload,
            }
        case 'grade':
            return {
                ...state,
                grade: action.payload,
                category: {value: "", label: ""},
            }
        case 'gradeNumericLiteracy':
            return {
                ...state,
                grade: action.payload,
                subject: {value: "", label: ""},
            }
        case 'categories':
            return {
                ...state,
                categories: action.payload,
                category: {value: "", label: ""},
                test_duration: {value: "", label: ""},
            }
        case 'category':
            return {
                ...state,
                category: action.payload,
            }
        case 'tag':
            return {
                ...state,
                tag: action.payload,
            }
        case 'variant':
            return {
                ...state,
                variant: action.payload,
            }
            case 'items_ids':
                return {
                    ...state,
                    items_ids: action.payload,
                }
        case 'RESET':
            return getInitialTestFormState();
        default:
            return state;
    }
}

const useCreateTest = ({
                           type,
                           multipleClassrooms = false,
                           isWorkPlan = false,
                           setError,
                           clearErrors,
                       }: UseCreateTestFormOptions) => {
    const [state, dispatch] = useReducer(reducer, getInitialTestFormState());

    const dataFetching = UseTestFormDataFetching({type: type, dispatch: dispatch, state: state});
    const {fetchCategoriesOnDifficultyChange, clearItems, items, setLoading} = dataFetching;

    const classroomSelection = UseClassroomSelection({users: state.users, dispatch: dispatch, setLoading});

    const stepsWizard = useCreateTestStepsWizard({
        type: type,
        individualTestValue: state.individual_test.value,
        setLoading: setLoading,
        setItemsHandler: dataFetching.setItemsHandler,
        clearItems: dataFetching.clearItems});
    const {setStep, handleStepTransition} = stepsWizard;

    const formSubmit = UseCreateTestFormSubmit({
        type: type,
        dispatch: dispatch,
        state: state,
        multipleClassrooms,
        items,
        setError,
        clearErrors,
        handleStepTransition});
    const {setSuccessMessage,} = formSubmit;

    const handleSelectTestType = (e: SelectChangeEvent) => {
        const target = e.target as HTMLInputElement;
        const {name, value} = target;

        dispatch({
            type: "type",
            payload: {value: value, label: name},
        });
    }

    const handleSelectChange = async (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        const {name, value} = target;

        dispatch({
            type: name,
            payload: {value: value, label: name},
        });

        if (state.grade.value && state.difficulty.value && name === "difficulty") {
            await fetchCategoriesOnDifficultyChange(value);
        }
    };

    const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        const {name, value} = target;

        dispatch({
            type: name,
            payload: value,
        });
    };

    const handleTestStartDate = (date: Date) => {
        dispatch({
            type: 'start_date',
            payload: date,
        })
    };

    const setSelectedVariant = (event: SelectChangeEvent) => {
        dispatch({
            type: 'variant',
            payload: {value:  event.target.value, label: "variant"},
        });
    };

    const setSelectedStandardsHandler = (
        event: React.ChangeEvent<HTMLInputElement>,
        categoryIndex: number,
        subIndex: number,
        competenceIndex: number,
    ) => {
        const {name: standardId, checked} = event.target;

        if ((checked && selectedStandardsCount(state.categories) >= MAX_SELECTED_STANDARDS) ||
            (checked && isWorkPlan && selectedStandardsCount(state.categories) >= 1)) {
            return;
        }

        const updatedCategories: StandardType[] = updateItemAtIndex(
            state.categories,
            categoryIndex,
            (category) => ({
                ...category,
                sub: updateItemAtIndex(category.sub, subIndex, (sub) => ({
                    ...sub,
                    competenta: updateItemAtIndex(sub.competenta, competenceIndex, (competence) => ({
                        ...competence,
                        standarde: competence.standarde?.map((standard: any) =>
                            String(standard.id) === standardId
                                ? { ...standard, checked }
                                : standard,
                        ),
                    })),
                })),
            }),
        );

        dispatch({
            type: "categories",
            payload: updatedCategories,
        });
    };

    const setSelectedCategoriesHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const {name: selectedId, checked} = event.target;
        const isParentCategory = type === "digitalLiteracy" || type === "scientificLiteracy";

        const checkedCategories = state.categories.map((category: ICategory) => {
            if (isParentCategory) {
                if (String(category.id) !== selectedId) return category;
                return {
                    ...category,
                    checked,
                    subcategories: category.subcategories?.map((sub) => ({ ...sub, checked })),
                };
            }

            return {
                ...category,
                subcategories: category.subcategories?.map((sub) =>
                    String(sub.id) === selectedId ? { ...sub, checked } : sub,
                ),
            };
        });

        dispatch({
            type: "categories",
            payload: checkedCategories,
        })
    }, [type, state.categories, dispatch]);

    const setSelectedCategoryCustomTestHandler = (data: string) => {
        dispatch({
            type: "category",
            payload: {value: data, label: "category"},
        })
    };

    const resetFormData = () => {
        setStep(1);
        setSuccessMessage("");
        clearItems();
        dispatch({
            type: 'RESET',
        });
    }

    //Multiple classrooms test generator helper functions
    const handleTestStartDateMultipleClassrooms = (date: Date, classroomId: any) => {
        let startDate = state.users.map((item: any) => {
            if (classroomId === item.id) {
                return {
                    ...item,
                    start_date: date,
                }
            } else {
                return item;
            }
        });
        dispatch({
            type: 'users',
            payload: startDate,
        });
    };

    const selectedCategoriesCount = state.categories.reduce((acc: number, category: any) => {
        const isStandard = state.categories.some((cat: any) => Array.isArray(cat.sub));

        if (isStandard) {
            return selectedStandardsCount(state.categories);
        }

        return state.categories.reduce((acc: number, category: any) => {
            if (category.categories) {
                return acc + category.categories.reduce((catAcc: number, subCat: any) => {
                    return catAcc + (subCat.subcategories?.filter((item: any) => item.checked).length || 0);
                }, 0);
            }
            return acc + (category.subcategories?.filter((item: any) => item.checked).length || 0);
        }, 0);
    }, 0);

    return {
        ...dataFetching,
        ...classroomSelection,
        ...stepsWizard,
        ...formSubmit,
        createTestData: state,
        selectedCategoriesCount,
        handleSelectChange,
        handleInputChange,
        setSelectedCategoriesHandler,
        setSelectedCategoryCustomTestHandler,
        handleTestStartDate,
        handleSelectTestType,
        resetFormData,
        handleTestStartDateMultipleClassrooms,
        setSelectedVariant,
        setSelectedStandardsHandler,
    };
};

export default useCreateTest;
